import { Breadcrumb, Button, Space } from 'antd';
import type { ReactNode } from 'react';
import { LifecycleBadge } from '@components/lifecycle/LifecycleBadge';
import type { LifecycleStatus } from '@/types/api';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface PageHeaderAction {
  key: string;
  label: string;
  icon?: ReactNode;
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  status?: LifecycleStatus;
  version?: number;
  breadcrumb?: BreadcrumbItem[];
  primaryAction?: PageHeaderAction;
  secondaryActions?: PageHeaderAction[];
  extra?: ReactNode;
}

/**
 * Standard page header for every detail/create page.
 * See docs/ui-ux-design.md §4.3.
 */
export function PageHeader({
  title,
  subtitle,
  status,
  version,
  breadcrumb,
  primaryAction,
  secondaryActions = [],
  extra,
}: PageHeaderProps) {
  return (
    <div
      style={{
        background: '#fff',
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
      }}
    >
      {breadcrumb && breadcrumb.length > 0 && (
        <Breadcrumb
          items={breadcrumb.map((b) => ({
            title: b.href ? <a href={b.href}>{b.label}</a> : b.label,
          }))}
          style={{ marginBottom: 8 }}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 600,
                color: '#0f172a',
              }}
            >
              {title}
            </h1>
            {status && version !== undefined && <LifecycleBadge status={status} version={version} />}
            {status && version === undefined && <LifecycleBadge status={status} />}
          </div>
          {subtitle && (
            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                color: '#64748b',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        <Space>
          {secondaryActions.map((a) => (
            <Button
              key={a.key}
              type={a.type ?? 'default'}
              icon={a.icon}
              {...(a.danger !== undefined ? { danger: a.danger } : {})}
              {...(a.disabled !== undefined ? { disabled: a.disabled } : {})}
              {...(a.onClick ? { onClick: a.onClick } : {})}
            >
              {a.label}
            </Button>
          ))}
          {primaryAction && (
            <Button
              type={primaryAction.type ?? 'primary'}
              icon={primaryAction.icon}
              {...(primaryAction.danger !== undefined ? { danger: primaryAction.danger } : {})}
              {...(primaryAction.disabled !== undefined ? { disabled: primaryAction.disabled } : {})}
              {...(primaryAction.onClick ? { onClick: primaryAction.onClick } : {})}
            >
              {primaryAction.label}
            </Button>
          )}
          {extra}
        </Space>
      </div>
    </div>
  );
}